import { withRequestDeadline } from "./requestDeadline";
import type { User } from "firebase/auth";
import {
  creationIntent,
  internalReturnPath,
  pricingDestination,
} from "./onboardingIntent";

const fields = [
  "articleUrl",
  "inputText",
  "templateId",
  "pendingContent",
  "sourceDocument",
  "claimPost",
  "claimToken",
  "intent",
  "claimIntent",
  "source",
  "tab",
  "preserveInputTextAsCaption",
  "toolUrl",
  "shareClone",
  "planId",
  "privateCode",
];

/** One auth handoff reader for existing sessions, Google, and email links. */
export function websiteHandoffParams(input: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of fields) {
    const value = input.get(key);
    if (value) params.set(key, value);
  }
  // Support in-flight links created by the existing shared-post flow.
  const returnPath = internalReturnPath(input.get("returnTo"));
  const returnUrl = returnPath ? new URL(returnPath, "https://app.invalid") : null;
  const returningToPrivateContent =
    (returnUrl?.pathname === "/created" && !!returnUrl.searchParams.get("viewPost")) ||
    (returnUrl?.pathname === "/ideas" && !!returnUrl.searchParams.get("viewIdea"));
  if (
    !returningToPrivateContent &&
    !params.has("shareClone") &&
    !params.has("claimPost") &&
    !creationIntent(params.toString())
  ) {
    const clone = sessionStorage.getItem("pendingShareClone");
    const template = sessionStorage.getItem("pendingSkipToGenerateTemplateId");
    if (clone) params.set("shareClone", clone);
    else if (sessionStorage.getItem("pendingSkipToGenerate") && template)
      params.set("templateId", template);
  }
  return params;
}

export function handoffToolUrl(params: URLSearchParams): string {
  try {
    const candidate = new URL(params.get("toolUrl") || "");
    if (
      !candidate.username &&
      !candidate.password &&
      ((candidate.protocol === "https:" &&
        ["wavegen.ai", "www.wavegen.ai", "app.wavegen.ai"].includes(
          candidate.hostname,
        )) ||
        (candidate.protocol === "http:" &&
          ["localhost", "127.0.0.1"].includes(candidate.hostname)))
    ) {
      return `${candidate.origin}${candidate.pathname}`;
    }
  } catch {
    /* Older links only carry a source slug. */
  }
  const source = params.get("source") || "";
  return `https://wavegen.ai/${/^[a-z0-9-]+$/.test(source) && source !== "homepage" ? source : ""}`;
}

export class WebsiteHandoffError extends Error {
  constructor(public toolUrl: string) {
    super(
      "We couldn’t restore your content. Return to the tool and try again.",
    );
  }
}

/** Replace the failed handoff so browser Back cannot repeat it. */
export function redirectToHandoffTool(toolUrl: string): void {
  const url = new URL(handoffToolUrl(new URLSearchParams({ toolUrl })));
  url.searchParams.set("handoff", "retry");
  window.location.replace(url.toString());
}

export async function resumeWebsiteHandoff(
  user: Pick<User, "getIdToken"> | null,
  params: URLSearchParams,
  returnTo: string,
): Promise<string> {
  const fallback = internalReturnPath(returnTo) || "/generate";
  const planId = params.get("planId");
  // Defer claiming until checkout is left; Back and checkout success both use
  // the same login continuation, with the plan removed so it cannot loop.
  if (planId) {
    sessionStorage.removeItem("pendingPlanId");
    const remaining = new URLSearchParams(params);
    remaining.delete("planId");
    remaining.set("returnTo", fallback);
    return pricingDestination(planId, `/login?${remaining}`);
  }
  const postId = params.get("claimPost");
  const shareId = params.get("shareClone");
  if (postId || shareId) {
    try {
      if (!user || (postId && !params.get("claimToken")))
        throw new Error("Missing claim");
      const body = await withRequestDeadline(async (signal) => {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE || "https://api.wavegen.ai"}${
          postId
            ? `/api/posts/${encodeURIComponent(postId)}/claim`
            : `/api/share/${encodeURIComponent(shareId!)}/clone`
        }`,
        {
          method: "POST",
          signal,
          headers: {
            Authorization: `Bearer ${await user.getIdToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            postId
              ? {
                  claimToken: params.get("claimToken"),
                  intent: params.get("intent") || params.get("claimIntent"),
                }
              : {},
          ),
        },
      );
      if (!response.ok) throw new Error("Claim failed");
      return response.json();
      }, 20000, "Restoring your content took too long. Please try again.");
      if (body.pendingContent && body.claimToken) {
        const next = new URLSearchParams({
          pendingContent: body.pendingContent,
          claimToken: body.claimToken,
        });
        for (const key of ["source", "toolUrl"]) {
          const value = params.get(key);
          if (value) next.set(key, value);
        }
        if (body.templateId) next.set("templateId", body.templateId);
        return `/generate?${next}`;
      }
      const id = postId || body.postId;
      if (!id) throw new Error("Missing post");
      for (const key of [
        "claimPost",
        "claimToken",
        "claimIntent",
        "pendingShareClone",
      ])
        sessionStorage.removeItem(key);
      const intent = params.get("intent") || params.get("claimIntent");
      if (intent === "remove-watermark")
        return `/created?viewPost=${encodeURIComponent(id)}`;
      return `/create-post?${new URLSearchParams({ draftId: id, ...(intent ? { intent } : {}) })}`;
    } catch {
      // Recovery returns to the tool for a fresh transfer. Do not replay a
      // failed legacy clone on every subsequent login in this tab.
      if (shareId && sessionStorage.getItem("pendingShareClone") === shareId) {
        sessionStorage.removeItem("pendingShareClone");
      }
      throw new WebsiteHandoffError(handoffToolUrl(params));
    }
  }
  for (const key of [
    "pendingContent",
    "claimToken",
    "claimIntent",
    "pendingSkipToGenerate",
    "pendingSkipToGenerateTemplateId",
    "pendingPlanId",
    "pendingArticleUrl",
  ])
    sessionStorage.removeItem(key);
  const intent = creationIntent(params.toString());
  if (intent) return `/generate?${intent}`;
  // Incomplete transfer links are a recovery state, not empty signup.
  if (params.has("pendingContent") || params.has("claimToken"))
    throw new WebsiteHandoffError(handoffToolUrl(params));
  return fallback;
}
